import React, { useEffect, useMemo, useState } from 'react';
// import { Layout, Typography, Tabs, Button, Card, Table, Space, message, Modal, Form, Input, Select, DatePicker, Dropdown, Tag } from 'antd';
import { Layout, Typography, Tabs, Button, Card, Table, Space, message, Modal, Form, Input, Select, DatePicker, Dropdown, Tag, Switch, Menu, InputNumber } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, MoreOutlined, LogoutOutlined, EnvironmentOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import api from '../api';
import { PrinterOutlined } from '@ant-design/icons';

const { Header, Content } = Layout;
const { Title } = Typography;

const GOOGLE_MAPS_API_KEY = 'AIzaSyBukqAGI9NioKWUOgzVs0vXrBOg9DnbwLo';
let gmapsLoading = false;
let gmapsReady = false;
const ensureGoogleMaps = () => new Promise((resolve) => {
  if (gmapsReady || window.google?.maps) { gmapsReady = true; return resolve(); }
  if (gmapsLoading) {
    const id = setInterval(() => {
      if (window.google?.maps) { clearInterval(id); gmapsReady = true; resolve(); }
    }, 50);
    return;
  }
  gmapsLoading = true;
  const s = document.createElement('script');
  s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(GOOGLE_MAPS_API_KEY)}&libraries=places`;
  s.async = true; s.defer = true;
  s.onload = () => { gmapsReady = true; resolve(); };
  document.body.appendChild(s);
});


export default function Sales() {
  const navigate = useNavigate();

  // Clients
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientOpen, setClientOpen] = useState(false);
  const [clientSaving, setClientSaving] = useState(false);
  const [clientForm] = Form.useForm();
  const [editingClient, setEditingClient] = useState(null);

  // Assignments
  const [assignments, setAssignments] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignForm] = Form.useForm();
  const [staffOptions, setStaffOptions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [editingAssignment, setEditingAssignment] = useState(null);

  // Targets
  const [targets, setTargets] = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [targetOpen, setTargetOpen] = useState(false);
  const [targetSaving, setTargetSaving] = useState(false);
  const [targetForm] = Form.useForm();
  const [editingTarget, setEditingTarget] = useState(null);

  const [visits, setVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [visitStaffFilter, setVisitStaffFilter] = useState(null);
  const [visitSearch, setVisitSearch] = useState('');
  const [visitDeptFilter, setVisitDeptFilter] = useState(null);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  // Per-tab filter states
  const [assignSearch, setAssignSearch] = useState('');
  const [assignDeptFilter, setAssignDeptFilter] = useState(null);
  const [assignStaffFilter, setAssignStaffFilter] = useState(null);
  const [targetSearch, setTargetSearch] = useState('');
  const [targetDeptFilter, setTargetDeptFilter] = useState(null);
  const [targetStaffFilter, setTargetStaffFilter] = useState(null);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderDeptFilter, setOrderDeptFilter] = useState(null);
  const [orderStaffFilter, setOrderStaffFilter] = useState(null);
  const [approvalSearch, setApprovalSearch] = useState('');
  const [approvalDeptFilter, setApprovalDeptFilter] = useState(null);
  const [approvalStaffFilter, setApprovalStaffFilter] = useState(null);

  // Incentive Approvals
  const [approvals, setApprovals] = useState([]);
  const [loadingApprovals, setLoadingApprovals] = useState(false);

  const [viewOrder, setViewOrder] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);

  // Visit detail modal state
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [visitDetailOpen, setVisitDetailOpen] = useState(false);
  const [visitDetailLoading, setVisitDetailLoading] = useState(false);

  const printRef = React.useRef(null);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  // Load clients
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setClientsLoading(true);
        const resp = await api.get('/admin/sales/clients');
        if (!mounted) return;
        const list = resp?.data?.clients || [];
        setClients(Array.isArray(list) ? list : []);
      } catch (e) {
        message.error(e?.response?.data?.message || 'Failed to load clients');
      } finally {
        setClientsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load assignments
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setAssignmentsLoading(true);
        const resp = await api.get('/admin/sales/assignments');
        if (!mounted) return;
        setAssignments(Array.isArray(resp?.data?.assignments) ? resp.data.assignments : []);
      } catch (e) {
        message.error(e?.response?.data?.message || 'Failed to load assignments');
      } finally {
        setAssignmentsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load targets
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setTargetsLoading(true);
        const resp = await api.get('/admin/sales/targets');
        if (!mounted) return;
        setTargets(Array.isArray(resp?.data?.targets) ? resp.data.targets : []);
      } catch (e) {
        message.error(e?.response?.data?.message || 'Failed to load sales targets');
      } finally {
        setTargetsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setVisitsLoading(true);
        const resp = await api.get('/admin/sales/visits');
        if (!mounted) return;
        setVisits(Array.isArray(resp?.data?.visits) ? resp.data.visits : []);
      } catch (e) {
        message.error(e?.response?.data?.message || 'Failed to load visits');
      } finally {
        setVisitsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setOrdersLoading(true);
        const resp = await api.get('/admin/sales/orders');
        if (!mounted) return;
        setOrders(Array.isArray(resp?.data?.orders) ? resp.data.orders : []);
      } catch (e) {
        message.error(e?.response?.data?.message || 'Failed to load orders');
      } finally {
        setOrdersLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load departments from business functions
  useEffect(() => {
    (async () => {
      try {
        const resp = await api.get('/admin/business-functions');
        const list = resp?.data?.data || [];
        const deptFn = list.find(f => String(f.name || '').toLowerCase() === 'department');
        const vals = Array.isArray(deptFn?.values) ? deptFn.values : [];
        setDepartments(vals.filter(v => v?.value).map(v => v.value));
      } catch (_) {}
    })();
  }, []);


  // Load incentive approvals
  const loadApprovals = async () => {
    try {
      setLoadingApprovals(true);
      const resp = await api.get('/admin/sales-incentives/approvals');
      if (resp.data.success) setApprovals(resp.data.approvals);
    } catch (e) {
      message.error('Failed to load incentive approvals');
    } finally {
      setLoadingApprovals(false);
    }
  };

  useEffect(() => { loadApprovals(); }, []);

  const handleUpdateApprovalStatus = async (id, status) => {
    try {
      await api.put(`/admin/sales-incentives/approvals/${id}`, { status });
      message.success(`Approval ${status}`);
      loadApprovals();
    } catch (e) {
      message.error('Failed to update approval');
    }
  };

  const handleUpdateIncentiveAmount = async (id, amount) => {
    try {
      await api.put(`/admin/sales-incentives/approvals/${id}`, { incentiveAmount: amount });
      loadApprovals();
    } catch (e) {
      message.error('Failed to update incentive amount');
    }
  };

  const openNewClient = () => {
    setEditingClient(null);
    clientForm.resetFields();
    setClientOpen(true);
  };

  const openEditClient = (row) => {
    setEditingClient(row);
    clientForm.setFieldsValue({
      name: row.name || '',
      phone: row.phone || '',
      clientType: row.clientType || '',
      location: row.location || '',
    });
    setClientOpen(true);
  };

  const openEditTarget = (row) => {
    setEditingTarget(row);
    targetForm.setFieldsValue({
      staffUserId: row.staffUserId,
      period: row.period,
      periodDate: row.periodDate ? dayjs(row.periodDate) : undefined,
      targetAmount: row.targetAmount,
      targetOrders: row.targetOrders,
    });
    setTargetOpen(true);
  };

  const saveClient = async () => {
    try {
      const v = await clientForm.validateFields();
      setClientSaving(true);
      const payload = {
        name: v.name,
        phone: v.phone,
        clientType: v.clientType,
        location: v.location,
      };
      let resp;
      if (editingClient && editingClient.id) {
        resp = await api.put(`/admin/sales/clients/${encodeURIComponent(String(editingClient.id))}`, payload);
      } else {
        resp = await api.post('/admin/sales/clients', payload);
      }
      if (resp?.data?.success) {
        message.success(editingClient ? 'Client updated' : 'Client created');
        setClientOpen(false);
        setClientsLoading(true);
        const r2 = await api.get('/admin/sales/clients', { params: { t: Date.now() } });
        setClients(Array.isArray(r2?.data?.clients) ? r2.data.clients : []);
      } else {
        message.error(resp?.data?.message || 'Failed to save client');
      }
    } catch (e) {
      if (!e?.errorFields) message.error(e?.response?.data?.message || 'Failed to save client');
    } finally {
      setClientSaving(false);
      setClientsLoading(false);
    }
  };

  const toggleClientActive = async (row) => {
    try {
      const nextActive = !(row.active === true);
      const resp = await api.put(`/admin/sales/clients/${encodeURIComponent(String(row.id))}`, { active: nextActive });
      if (resp?.data?.success) {
        const r2 = await api.get('/admin/sales/clients', { params: { t: Date.now() } });
        setClients(Array.isArray(r2?.data?.clients) ? r2.data.clients : []);
        message.success(nextActive ? 'Client activated' : 'Client deactivated');
      } else {
        message.error(resp?.data?.message || 'Failed to update');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to update');
    }
  };

  // Assignments handlers
  const openAssign = async (row) => {
    try {
      assignForm.resetFields();
      setAssignOpen(true);
      setEditingAssignment(row || null);
      if (row) {
        assignForm.setFieldsValue({
          clientId: row.clientId,
          staffUserId: row.staffUserId,
          title: row.title,
          description: row.description,
          status: row.status,
          assignedOn: row.assignedOn ? dayjs(row.assignedOn) : undefined,
          dueDate: row.dueDate ? dayjs(row.dueDate) : undefined,
          clientAddress: row.clientAddress || '',
          clientLat: row.clientLat,
          clientLng: row.clientLng,
        });
      }
      const r = await api.get('/admin/staff');
      const list = Array.isArray(r?.data?.staff) ? r.data.staff : [];
      setStaffOptions(list.map(u => ({ value: u.id, label: u.name || u.phone || `User #${u.id}` })));
    } catch (_) {
      setStaffOptions([]);
    }
  };

  const saveAssignment = async () => {
    try {
      const v = await assignForm.validateFields();
      setAssignSaving(true);
      const payload = {
        clientId: v.clientId,
        staffUserId: v.staffUserId,
        title: v.title,
        description: v.description,
        status: v.status,
        assignedOn: v.assignedOn ? v.assignedOn.format('YYYY-MM-DD HH:mm:ss') : undefined,
        dueDate: v.dueDate ? v.dueDate.format('YYYY-MM-DD HH:mm:ss') : undefined,
        clientAddress: v.clientAddress,
        clientLat: v.clientLat,
        clientLng: v.clientLng,
      };
      let resp;
      if (editingAssignment && editingAssignment.id) {
        resp = await api.put(`/admin/sales/assignments/${encodeURIComponent(String(editingAssignment.id))}`, payload);
      } else {
        resp = await api.post('/admin/sales/assignments', payload);
      }
      if (resp?.data?.success) {
        message.success(editingAssignment ? 'Assignment updated' : 'Assignment created');
        setAssignOpen(false);
        setAssignmentsLoading(true);
        const r2 = await api.get('/admin/sales/assignments');
        setAssignments(Array.isArray(r2?.data?.assignments) ? r2.data.assignments : []);
      } else {
        message.error(resp?.data?.message || 'Failed to assign');
      }
    } catch (e) {
      if (!e?.errorFields) message.error(e?.response?.data?.message || 'Failed to assign');
    } finally {
      setAssignSaving(false);
      setAssignmentsLoading(false);
    }
  };

  const deleteAssignment = (row) => {
    Modal.confirm({
      title: 'Delete assignment?',
      content: 'This action cannot be undone.',
      okType: 'danger',
      onOk: async () => {
        try {
          const resp = await api.delete(`/admin/sales/assignments/${encodeURIComponent(String(row.id))}`);
          if (resp?.data?.success) {
            message.success('Assignment deleted');
            setAssignmentsLoading(true);
            const r2 = await api.get('/admin/sales/assignments');
            setAssignments(Array.isArray(r2?.data?.assignments) ? r2.data.assignments : []);
          } else {
            message.error(resp?.data?.message || 'Failed to delete assignment');
          }
        } catch (e) {
          message.error(e?.response?.data?.message || 'Failed to delete assignment');
        } finally {
          setAssignmentsLoading(false);
        }
      },
    });
  };

  // Targets handlers
  const openNewTarget = async () => {
    try {
      targetForm.resetFields();
      setTargetOpen(true);
      if (!staffOptions.length) {
        const r = await api.get('/admin/staff');
        const list = Array.isArray(r?.data?.staff) ? r.data.staff : [];
        setStaffOptions(list.map(u => ({ value: u.id, label: u.name || u.phone || `User #${u.id}` })));
      }
    } catch (_) { }
  };

  const saveTarget = async () => {
    try {
      const v = await targetForm.validateFields();
      setTargetSaving(true);
      const payload = {
        staffUserId: v.staffUserId,
        period: v.period,
        periodDate: v.periodDate ? v.periodDate.format('YYYY-MM-DD') : undefined,
        targetAmount: Number(v.targetAmount || 0),
        targetOrders: Number(v.targetOrders || 0),
      };
      let resp;
      if (editingTarget && editingTarget.id) {
        resp = await api.put(`/admin/sales/targets/${encodeURIComponent(String(editingTarget.id))}`, payload);
      } else {
        resp = await api.post('/admin/sales/targets', payload);
      }
      if (resp?.data?.success) {
        message.success(editingTarget ? 'Target updated' : 'Target created');
        setTargetOpen(false);
        setEditingTarget(null);
        setTargetsLoading(true);
        const r2 = await api.get('/admin/sales/targets');
        setTargets(Array.isArray(r2?.data?.targets) ? r2.data.targets : []);
      } else {
        message.error(resp?.data?.message || 'Failed to save target');
      }
    } catch (e) {
      if (!e?.errorFields) message.error(e?.response?.data?.message || 'Failed to save target');
    } finally {
      setTargetSaving(false);
      setTargetsLoading(false);
    }
  };

  const deleteTarget = (row) => {
    Modal.confirm({
      title: 'Delete target?',
      content: 'This action cannot be undone.',
      okType: 'danger',
      onOk: async () => {
        try {
          const resp = await api.delete(`/admin/sales/targets/${encodeURIComponent(String(row.id))}`);
          if (resp?.data?.success) {
            message.success('Target deleted');
            setTargetsLoading(true);
            const r2 = await api.get('/admin/sales/targets');
            setTargets(Array.isArray(r2?.data?.targets) ? r2.data.targets : []);
          } else {
            message.error(resp?.data?.message || 'Failed to delete target');
          }
        } catch (e) {
          message.error(e?.response?.data?.message || 'Failed to delete target');
        } finally {
          setTargetsLoading(false);
        }
      }
    });
  };

  // Google Places Autocomplete for Assignment Modal
  const acAttached = React.useRef(false);
  useEffect(() => {
    if (!assignOpen) {
      acAttached.current = false;
      return;
    }
    const timer = setTimeout(async () => {
      await ensureGoogleMaps();
      const el = document.getElementById('assign-client-address');
      if (el && window.google?.maps?.places && !acAttached.current) {
        try {
          const ac = new window.google.maps.places.Autocomplete(el, { fields: ['formatted_address', 'geometry'] });
          ac.addListener('place_changed', () => {
            const place = ac.getPlace();
            const loc = place?.geometry?.location;
            if (loc) {
              assignForm.setFieldsValue({
                clientAddress: place.formatted_address || el.value,
                clientLat: loc.lat(),
                clientLng: loc.lng(),
              });
            }
          });
          acAttached.current = true;
        } catch (_) { }
      }
    }, 500); // Wait for modal animation
    return () => clearTimeout(timer);
  }, [assignOpen, assignForm]);

  const openViewOrder = async (row) => {
    try {
      // Fetch full order detail from backend admin endpoint
      const resp = await api.get(`/admin/sales/orders/${encodeURIComponent(String(row.id))}`);
      if (resp?.data?.success && resp.data.order) {
        setViewOrder(resp.data.order);
        setViewOpen(true);
      } else {
        message.error(resp?.data?.message || 'Failed to load order');
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to load order');
    }
  };

  const openViewVisit = async (row) => {
    try {
      setVisitDetailLoading(true);
      setVisitDetailOpen(true);
      // Fetch full visit detail from backend admin endpoint
      const resp = await api.get(`/admin/sales/visits/${encodeURIComponent(String(row.id))}`);
      if (resp?.data?.success && resp.data.visit) {
        setSelectedVisit(resp.data.visit);
      } else {
        message.error(resp?.data?.message || 'Failed to load visit details');
        setVisitDetailOpen(false);
      }
    } catch (e) {
      message.error(e?.response?.data?.message || 'Failed to load visit details');
      setVisitDetailOpen(false);
    } finally {
      setVisitDetailLoading(false);
    }
  };

  // Clients table columns
  const clientColumns = [
    { title: 'Name', dataIndex: 'name' },
    { title: 'Phone', dataIndex: 'phone' },
    { title: 'Type', dataIndex: 'clientType' },
    { title: 'Location', dataIndex: 'location' },
    { title: 'Active', dataIndex: 'active', render: (v) => v ? <Tag color="green">Active</Tag> : <Tag color="red">Inactive</Tag> },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => {
        const active = row.active === true;
        return (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                { key: 'edit', label: 'Edit', onClick: () => openEditClient(row) },
                { key: 'toggle', label: active ? 'Deactivate' : 'Activate', onClick: () => toggleClientActive(row) },
              ]
            }}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        );
      }
    }
  ];

  // Assignments table columns
  const assignmentColumns = [
    { title: 'Client', dataIndex: 'clientName' },
    { title: 'Staff', dataIndex: 'staffName', render: (_, r) => getRecordStaffName(r) || '-' },
    { title: 'Title', dataIndex: 'title' },
    { title: 'Status', dataIndex: 'status' },
    { title: 'Assigned On', dataIndex: 'assignedOn', render: (v) => v ? dayjs(v).format('DD MMMM YYYY hh:mm A') : '' },
    { title: 'Due Date', dataIndex: 'dueDate', render: (v) => v ? dayjs(v).format('DD MMMM YYYY hh:mm A') : '' },
    {
      title: 'Actions',
      key: 'a',
      render: (_, row) => (
        <Dropdown
          menu={{
            items: [
              { key: 'edit', label: 'Edit', onClick: () => openAssign(row) },
              { key: 'delete', label: 'Delete', onClick: () => deleteAssignment(row) },
            ]
          }}
          trigger={['click']}
        >
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      )
    },
  ];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get('/admin/staff');
        if (!mounted) return;
        const list = Array.isArray(r?.data?.staff) ? r.data.staff : [];
        setStaffOptions(list.map(u => ({ value: u.id, label: u.name || u.phone || `User #${u.id}` })));
      } catch (_) {
        // ignore
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Targets table columns
  const targetsColumns = [
    { title: 'Staff', dataIndex: 'staffName', render: (v, r) => v || staffById[r.staffUserId] || (r.staffUserId ? `User #${r.staffUserId}` : '') },
    { title: 'Period', dataIndex: 'period', render: (v) => String(v || '').toUpperCase() },
    { title: 'Period Date', dataIndex: 'periodDate', render: (v) => v ? dayjs(v).format('DD MMMM YYYY') : '' },
    { title: 'Target', render: (_, r) => `${r.targetAmount ? `₹${r.targetAmount}` : 0}${r.targetOrders ? ` • ${r.targetOrders} orders` : ''}` },
    { title: 'Achieved', render: (_, r) => `${r.achievedAmount ? `₹${r.achievedAmount}` : 0}${r.achievedOrders ? ` • ${r.achievedOrders} orders` : ''}` },
    {
      title: 'Actions', key: 't_actions', render: (_, row) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: [
              { key: 'edit', label: 'Edit', onClick: () => openEditTarget(row) },
              { key: 'delete', label: 'Delete', onClick: () => deleteTarget(row) }
            ]
          }}
        >
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      )
    },
  ];

  const visitColumns = [
    { title: 'Date', dataIndex: 'visitDate', render: (v) => v ? dayjs(v).format('DD MMMM YYYY') : '' },
    { title: 'Staff', dataIndex: 'staffName', render: (v, r) => v || staffById[(r.userId || r.user_id || r.staffUserId)] || '' },
    { title: 'Client', dataIndex: 'clientName' },
    { title: 'Type', dataIndex: 'visitType' },
    {
      title: 'Location',
      dataIndex: 'location',
      render: (_, r) => {
        const addr = r.checkInAddress || r.location || '';
        const lat = r.checkInLat;
        const lng = r.checkInLng;
        const alt = r.checkInAltitude;
        const hasGeo = Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
        return (
          <div>
            <div>{addr || '-'}</div>
            {hasGeo ? (
              <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                {`Lat: ${lat}, Lng: ${lng}`}{alt ? `, Alt: ${alt}m` : ''}
              </div>
            ) : null}
          </div>
        );
      }
    },
    // { title: 'Verified', dataIndex: 'verified', render: (v) => v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag> },
    {
      title: 'Verified', dataIndex: 'verified', render: (v, r) => (
        <Switch
          checked={!!v}
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={async (checked) => {
            try {
              // Optimistic UI update
              setVisits(prev => prev.map(it => it.id === r.id ? { ...it, verified: checked } : it));
              const resp = await api.put(`/admin/sales/visits/${encodeURIComponent(String(r.id))}`, { verified: checked });
              if (!resp?.data?.success) throw new Error(resp?.data?.message || 'Failed');
              message.success(checked ? 'Marked as verified' : 'Marked as not verified');
            } catch (e) {
              // Revert on error
              setVisits(prev => prev.map(it => it.id === r.id ? { ...it, verified: !checked } : it));
              message.error(e?.response?.data?.message || 'Failed to update visit');
            }
          }}
        />
      )
    },
    // { title: 'Amount', dataIndex: 'amount', render: (v) => v ? `₹${v}` : 0 },
    {
      title: 'Actions',
      key: 'v_actions',
      render: (_, row) => (
        <Dropdown
          trigger={['click']}
          menu={{ items: [{ key: 'view', label: 'View', onClick: () => openViewVisit(row) }] }}
        >
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      ),
    }
  ];

  const approvalColumns = [
    {
      title: 'Staff Member',
      key: 'staff',
      render: (_, record) => record.staff?.profile?.name || record.staff?.phone || 'Unknown'
    },
    { title: 'Rule', dataIndex: ['rule', 'name'], key: 'rule' },
    { title: 'Achieved', dataIndex: 'achievedAmount', key: 'achieved', render: (v) => `Rs ${v}` },
    {
      title: 'Incentive',
      key: 'incentive',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Rs</span>
          <InputNumber
            size="small"
            value={record.incentiveAmount}
            style={{ width: 100 }}
            onChange={(v) => handleUpdateIncentiveAmount(record.id, v)}
            disabled={record.status !== 'pending'}
          />
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (v) => {
        const colors = { pending: 'orange', approved: 'green', rejected: 'red' };
        return <Tag color={colors[v]}>{v?.toUpperCase()}</Tag>;
      }
    },
    { title: 'Date', dataIndex: 'createdAt', key: 'date', render: (v) => new Date(v).toLocaleDateString() },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        record.status === 'pending' && (
          <Space>
            <Button
              type="primary" size="small"
              onClick={() => handleUpdateApprovalStatus(record.id, 'approved')}
              style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >Approve</Button>
            <Button danger size="small" onClick={() => handleUpdateApprovalStatus(record.id, 'rejected')}>
              Reject
            </Button>
          </Space>
        )
      )
    }
  ];

  const orderColumns = [
    { title: 'Order Date', dataIndex: 'orderDate', render: (v) => v ? dayjs(v).format('DD MMMM YYYY') : '' },
    { title: 'Staff', dataIndex: 'staffName', render: (v, r) => v || staffById[(r.userId || r.user_id || r.staffUserId)] || '' },
    { title: 'Client', dataIndex: 'clientName', render: (v) => v || '' },
    { title: 'Items', dataIndex: 'items' },
    {
      title: 'Location',
      render: (_, r) => {
        const addr = r.checkInAddress || '';
        const lat = r.checkInLat;
        const lng = r.checkInLng;
        const alt = r.checkInAltitude;
        const hasGeo = Number.isFinite(Number(lat)) && Number.isFinite(Number(lng));
        return (
          <div>
            <div>{addr || '-'}</div>
            {hasGeo ? (
              <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                {`Lat: ${lat}, Lng: ${lng}`}{alt ? `, Alt: ${alt}m` : ''}
              </div>
            ) : null}
          </div>
        );
      }
    },
    { title: 'Net Amount', dataIndex: 'netAmount', render: (v) => v ? `₹${v}` : 0 },
    { title: 'GST Amount', dataIndex: 'gstAmount', render: (v) => v ? `₹${v}` : 0 },
    { title: 'Total Amount', dataIndex: 'totalAmount', render: (v) => v ? `₹${v}` : 0 },
    {
      title: 'Actions',
      key: 'o_actions',
      render: (_, row) => (
        <Dropdown
          trigger={['click']}
          menu={{ items: [{ key: 'view', label: 'View', onClick: () => openViewOrder(row) }] }}
        >
          <Button icon={<MoreOutlined />} />
        </Dropdown>
      ),
    }
  ];

  const staffById = React.useMemo(() => {
    const m = {};
    (staffOptions || []).forEach(o => {
      if (!o || o.value == null) return;
      m[o.value] = o.label;
      m[String(o.value)] = o.label;
      const n = Number(o.value);
      if (Number.isFinite(n)) m[n] = o.label;
    });
    return m;
  }, [staffOptions]);

  const getRecordStaffName = React.useCallback((r) => {
    if (!r) return '';
    const id = r.staffUserId ?? r.staff_user_id ?? r.userId ?? r.user_id ?? null;
    const mapped = id == null ? '' : String(staffById[id] || staffById[String(id)] || '');
    const direct = String(r.staffName || r.salesPerson || r?.staff?.profile?.name || '');
    // Prefer mapped staff label from master staff list (usually real employee name).
    if (mapped) return mapped;
    if (direct) return direct;
    return '';
  }, [staffById]);

  const tabs = useMemo(() => ([
    {
      key: 'clients',
      label: 'Clients',
      children: (
        <Card title="Clients" extra={<Button type="primary" icon={<PlusOutlined />} onClick={openNewClient}>New Client</Button>}>
          <Table rowKey={(r) => r.id} loading={clientsLoading} columns={clientColumns} dataSource={clients} pagination={false} />
        </Card>
      )
    },
    {
      key: 'assignments',
      label: 'Assign Staff',
      children: (() => {
        const deptOptions = departments.map(d => ({ label: d, value: d }));
        const filtered = assignments.filter(a => {
          const name = getRecordStaffName(a).toLowerCase();
          return (!assignSearch || name.includes(assignSearch.toLowerCase()))
            && (!assignStaffFilter || a.staffUserId === assignStaffFilter || a.staffUserId === Number(assignStaffFilter));
        });
        return (
          <Card title="Client Assignments" extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openAssign(null)}>Assign Staff</Button>}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Input.Search placeholder="Search by employee" allowClear style={{ width: 220 }} value={assignSearch} onChange={e => setAssignSearch(e.target.value)} />
              <Select placeholder="Filter by Department" allowClear style={{ width: 200 }} value={assignDeptFilter} onChange={v => setAssignDeptFilter(v ?? null)} options={deptOptions} />
              <Select placeholder="Filter by Employee" allowClear showSearch style={{ width: 200 }} value={assignStaffFilter} onChange={v => setAssignStaffFilter(v ?? null)} options={staffOptions} filterOption={(inp, opt) => (opt?.label ?? '').toLowerCase().includes(inp.toLowerCase())} />
            </div>
            <Table rowKey={(r) => r.id} loading={assignmentsLoading} columns={assignmentColumns} dataSource={filtered} pagination={{ pageSize: 10, showSizeChanger: true }} />
          </Card>
        );
      })()
    },
    {
      key: 'targets',
      label: 'Targets',
      children: (() => {
        const deptOptions = departments.map(d => ({ label: d, value: d }));
        const filtered = targets.filter(t => {
          const name = getRecordStaffName(t).toLowerCase();
          return (!targetSearch || name.includes(targetSearch.toLowerCase()))
            && (!targetStaffFilter || t.staffUserId === targetStaffFilter || t.staffUserId === Number(targetStaffFilter));
        });
        return (
          <Card title="Sales Targets" extra={<Button type="primary" icon={<PlusOutlined />} onClick={openNewTarget}>New Target</Button>}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Input.Search placeholder="Search by employee" allowClear style={{ width: 220 }} value={targetSearch} onChange={e => setTargetSearch(e.target.value)} />
              <Select placeholder="Filter by Department" allowClear style={{ width: 200 }} value={targetDeptFilter} onChange={v => setTargetDeptFilter(v ?? null)} options={deptOptions} />
              <Select placeholder="Filter by Employee" allowClear showSearch style={{ width: 200 }} value={targetStaffFilter} onChange={v => setTargetStaffFilter(v ?? null)} options={staffOptions} filterOption={(inp, opt) => (opt?.label ?? '').toLowerCase().includes(inp.toLowerCase())} />
            </div>
            <Table rowKey={(r) => r.id} loading={targetsLoading} columns={targetsColumns} dataSource={filtered} pagination={{ pageSize: 10, showSizeChanger: true }} />
          </Card>
        );
      })()
    },
    {
      key: 'visits',
      label: 'Visits',
      children: (() => {
        // Use departments fetched from business-functions API
        const deptOptions = departments.map(d => ({ label: d, value: d }));

        const filteredVisits = visits.filter(v => {
          const matchStaff   = visitStaffFilter ? (v.staffUserId === visitStaffFilter || v.staffUserId === Number(visitStaffFilter)) : true;
          const matchSearch  = visitSearch ? getRecordStaffName(v).toLowerCase().includes(visitSearch.toLowerCase()) : true;
          const matchDept    = visitDeptFilter ? (v.department || v.staffDepartment || '') === visitDeptFilter : true;
          return matchStaff && matchSearch && matchDept;
        });

        return (
          <Card title="Visits">
            {/* Filter toolbar */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Input.Search
                placeholder="Search by employee name"
                allowClear
                style={{ width: 220 }}
                value={visitSearch}
                onChange={e => setVisitSearch(e.target.value)}
              />
              <Select
                placeholder="Filter by Department"
                allowClear
                style={{ width: 200 }}
                value={visitDeptFilter}
                onChange={v => setVisitDeptFilter(v ?? null)}
                options={deptOptions.length ? deptOptions : staffOptions.map(s => ({ label: s.label, value: s.label }))}
              />
              <Select
                placeholder="Filter by Employee"
                allowClear
                style={{ width: 200 }}
                value={visitStaffFilter}
                onChange={v => setVisitStaffFilter(v ?? null)}
                options={staffOptions}
                showSearch
                filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
              />
            </div>
            <Table
              rowKey={(r) => r.id}
              loading={visitsLoading}
              columns={visitColumns}
              dataSource={filteredVisits}
              pagination={{ pageSize: 10, showSizeChanger: true }}
            />
          </Card>
        );
      })()
    },
    {
      key: 'orders',
      label: 'Orders',
      children: (() => {
        const deptOptions = departments.map(d => ({ label: d, value: d }));
        const filtered = orders.filter(o => {
          const name = getRecordStaffName(o).toLowerCase();
          return (!orderSearch || name.includes(orderSearch.toLowerCase()))
            && (!orderStaffFilter || o.userId === orderStaffFilter || o.userId === Number(orderStaffFilter) || o.staffUserId === orderStaffFilter || o.staffUserId === Number(orderStaffFilter));
        });
        return (
          <Card title="Orders">
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Input.Search placeholder="Search by employee" allowClear style={{ width: 220 }} value={orderSearch} onChange={e => setOrderSearch(e.target.value)} />
              <Select placeholder="Filter by Department" allowClear style={{ width: 200 }} value={orderDeptFilter} onChange={v => setOrderDeptFilter(v ?? null)} options={deptOptions} />
              <Select placeholder="Filter by Employee" allowClear showSearch style={{ width: 200 }} value={orderStaffFilter} onChange={v => setOrderStaffFilter(v ?? null)} options={staffOptions} filterOption={(inp, opt) => (opt?.label ?? '').toLowerCase().includes(inp.toLowerCase())} />
            </div>
            <Table rowKey={(r) => r.id} loading={ordersLoading} columns={orderColumns} dataSource={filtered} pagination={{ pageSize: 10, showSizeChanger: true }} />
          </Card>
        );
      })()
    },
    {
      key: 'approvals',
      label: 'Incentive Approvals',
      children: (() => {
        const deptOptions = departments.map(d => ({ label: d, value: d }));
        const filtered = (approvals || []).filter(a => {
          const name = (a.staff?.profile?.name || a.staff?.phone || '').toLowerCase();
          return (!approvalSearch || name.includes(approvalSearch.toLowerCase()))
            && (!approvalStaffFilter || a.staffUserId === approvalStaffFilter || a.staffUserId === Number(approvalStaffFilter));
        });
        return (
          <Card title="Incentive Approvals">
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              <Input.Search placeholder="Search by staff" allowClear style={{ width: 220 }} value={approvalSearch} onChange={e => setApprovalSearch(e.target.value)} />
              <Select placeholder="Filter by Department" allowClear style={{ width: 200 }} value={approvalDeptFilter} onChange={v => setApprovalDeptFilter(v ?? null)} options={deptOptions} />
              <Select placeholder="Filter by Employee" allowClear showSearch style={{ width: 200 }} value={approvalStaffFilter} onChange={v => setApprovalStaffFilter(v ?? null)} options={staffOptions} filterOption={(inp, opt) => (opt?.label ?? '').toLowerCase().includes(inp.toLowerCase())} />
            </div>
            <Table rowKey={(r) => r.id} loading={loadingApprovals} columns={approvalColumns} dataSource={filtered} pagination={{ pageSize: 10, showSizeChanger: true }} />
          </Card>
        );
      })()
    },
  ]), [clients, clientsLoading, assignments, assignmentsLoading, targets, targetsLoading, visits, visitsLoading, orders, ordersLoading, approvals, loadingApprovals, departments, staffOptions, assignSearch, assignDeptFilter, assignStaffFilter, targetSearch, targetDeptFilter, targetStaffFilter, visitSearch, visitDeptFilter, visitStaffFilter, orderSearch, orderDeptFilter, orderStaffFilter, approvalSearch, approvalDeptFilter, approvalStaffFilter, getRecordStaffName]);

  return (
    <Layout style={{ minHeight: '100vh', marginLeft: 200 }}>
      <Sidebar />
      <Layout>
        <Header style={{ background: '#fff', padding: '12px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Back to Dashboard
            </Button>
            <Title level={4} style={{ margin: 0 }}>Sales</Title>
          </div>
          <Menu
            theme="light"
            mode="horizontal"
            items={[
              {
                key: 'logout',
                icon: <LogoutOutlined />,
                label: 'Logout',
                onClick: handleLogout
              }
            ]}
            style={{ borderRight: 'none', backgroundColor: 'transparent' }}
          />
        </Header>
        <Content style={{ padding: 24 }}>
          <Tabs defaultActiveKey="clients" items={tabs} />
        </Content>
      </Layout>

      {/* Client Modal */}
      <Modal
        open={clientOpen}
        title={editingClient ? 'Edit Client' : 'New Client'}
        onCancel={() => setClientOpen(false)}
        onOk={saveClient}
        okText={editingClient ? 'Save' : 'Create'}
        confirmLoading={clientSaving}
        destroyOnClose
      >
        <Form form={clientForm} layout="vertical">
          <Form.Item name="name" label="Client Name" rules={[{ required: true, message: 'Enter client name' }]}>
            <Input placeholder="e.g., Sample Client" />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input placeholder="e.g., 9876543210" />
          </Form.Item>
          <Form.Item name="clientType" label="Client Type">
            <Input placeholder="e.g., Retail, Distributor, etc." />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input placeholder="e.g., Jaipur, Rajasthan" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Assignment Modal */}
      <Modal
        open={assignOpen}
        title={editingAssignment ? 'Edit Assignment' : 'Assign Staff'}
        onCancel={() => setAssignOpen(false)}
        onOk={saveAssignment}
        confirmLoading={assignSaving}
        okText={editingAssignment ? 'Save' : 'Assign'}
        destroyOnClose
      >
        <Form form={assignForm} layout="vertical">
          <Form.Item name="clientId" label="Client" rules={[{ required: true, message: 'Select client' }]}>
            <Select
              placeholder="Select client"
              showSearch
              optionFilterProp="label"
              options={clients.map(c => ({ value: c.id, label: c.name }))}
            />
          </Form.Item>
          <Form.Item name="staffUserId" label="Staff" rules={[{ required: true, message: 'Select staff' }]}>
            <Select placeholder="Select staff" options={staffOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Enter title' }]}>
            <Input placeholder="e.g., First Visit" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="Optional" />
          </Form.Item>
          <Form.Item name="status" label="Status" initialValue="pending">
            <Select
              options={[
                { value: 'pending', label: 'pending' },
                { value: 'inprogress', label: 'inprogress' },
                { value: 'complete', label: 'complete' },
              ]}
            />
          </Form.Item>
          <Form.Item name="assignedOn" label="Assigned On">
            <DatePicker style={{ width: '100%' }} showTime />
          </Form.Item>
          <Form.Item name="dueDate" label="Due Date">
            <DatePicker style={{ width: '100%' }} showTime />
          </Form.Item>
          <Form.Item name="clientAddress" label="Client Address">
            <Input id="assign-client-address" prefix={<EnvironmentOutlined />} placeholder="Search client address..." />
          </Form.Item>
          <Form.Item name="clientLat" hidden><Input /></Form.Item>
          <Form.Item name="clientLng" hidden><Input /></Form.Item>
        </Form>
      </Modal>

      {/* New Target Modal */}
      <Modal
        open={targetOpen}
        title={editingTarget ? 'Edit Sales Target' : 'New Sales Target'}
        onCancel={() => { setTargetOpen(false); setEditingTarget(null); }}
        onOk={saveTarget}
        confirmLoading={targetSaving}
        okText={editingTarget ? 'Save' : 'Save'}
        destroyOnClose
      >
        <Form layout="vertical" form={targetForm}>
          <Form.Item name="staffUserId" label="Staff" rules={[{ required: true, message: 'Select staff' }]}>
            <Select placeholder="Select staff" options={staffOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="period" label="Period" rules={[{ required: true, message: 'Select period' }]} initialValue="monthly">
            <Select
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
              ]}
            />
          </Form.Item>
          <Form.Item name="periodDate" label="Period Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="targetAmount" label="Target Amount (₹)">
            <Input type="number" placeholder="e.g., 25000" />
          </Form.Item>
          <Form.Item name="targetOrders" label="Target Orders">
            <Input type="number" placeholder="e.g., 40" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={viewOpen}
        title={`Order #${viewOrder?.id || ''}`}
        onCancel={() => { setViewOpen(false); setViewOrder(null); }}
        footer={null}
        width={720}
        destroyOnClose
      >
        {viewOrder ? (
          <div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Button onClick={() => {
                if (!printRef.current) return;
                const html = `
          <html>
            <head>
              <title>Order #${viewOrder?.id || ''}</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 16px; }
                b { font-weight: 600; }
                table { width: 100%; border-collapse: collapse; margin-top: 12px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background: #f5f5f5; }
                .totals { display: flex; justify-content: flex-end; gap: 24px; margin-top: 12px; }
              </style>
            </head>
            <body>${printRef.current.innerHTML}</body>
          </html>`;
                const w = window.open('', 'printWindow');
                if (w) {
                  w.document.open();
                  w.document.write(html);
                  w.document.close();
                  w.focus();
                  setTimeout(() => { w.print(); w.close(); }, 300);
                }
              }}>
                Print
              </Button>
            </div>

            <div ref={printRef}>
              <div style={{ marginBottom: 12 }}>
                <b>Order Date:</b> {viewOrder.orderDate ? dayjs(viewOrder.orderDate).format('DD MMMM YYYY hh:mm A') : ''}
              </div>
              <div style={{ marginBottom: 12 }}>
                <b>Staff:</b> {viewOrder.staffName || ''}
              </div>
              <div style={{ marginBottom: 12 }}>
                <b>Client:</b> {viewOrder.clientName || viewOrder.client?.name || ''}
              </div>
              <div style={{ marginBottom: 12 }}>
                <b>Payment:</b> {viewOrder.paymentMethod || '-'} | <b>Remarks:</b> {viewOrder.remarks || '-'}
              </div>
              <div style={{ marginBottom: 12 }}>
                <b>Location:</b> {viewOrder.checkInAddress || '-'}
                {(Number.isFinite(Number(viewOrder.checkInLat)) && Number.isFinite(Number(viewOrder.checkInLng))) ? (
                  <div style={{ color: '#8c8c8c', fontSize: 12, marginTop: 4 }}>
                    {`Lat: ${viewOrder.checkInLat}, Lng: ${viewOrder.checkInLng}`}{viewOrder.checkInAltitude ? `, Alt: ${viewOrder.checkInAltitude}m` : ''}
                  </div>
                ) : null}
              </div>
              <Table
                rowKey={(it) => it.id}
                size="small"
                pagination={false}
                columns={[
                  { title: 'Product', dataIndex: 'name' },
                  { title: 'Size', dataIndex: 'size' },
                  { title: 'Qty', dataIndex: 'qty' },
                  { title: 'Price', dataIndex: 'price', render: (v) => v ? `₹${v}` : 0 },
                  { title: 'Amount', dataIndex: 'amount', render: (v) => v ? `₹${v}` : 0 },
                ]}
                dataSource={Array.isArray(viewOrder.items) ? viewOrder.items : []}
              />
              <div className="totals" style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginTop: 12 }}>
                <div><b>Net:</b> {viewOrder.netAmount ? `₹${viewOrder.netAmount}` : 0}</div>
                <div><b>GST:</b> {viewOrder.gstAmount ? `₹${viewOrder.gstAmount}` : 0}</div>
                <div><b>Total:</b> {viewOrder.totalAmount ? `₹${viewOrder.totalAmount}` : 0}</div>
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Visit Detail Modal */}
      <Modal
        open={visitDetailOpen}
        title={`Visit Details #${selectedVisit?.id || ''}`}
        onCancel={() => { setVisitDetailOpen(false); setSelectedVisit(null); }}
        footer={null}
        width={800}
        destroyOnClose
      >
        {visitDetailLoading ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div>Loading visit details...</div>
          </div>
        ) : selectedVisit ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <strong>Visit Date:</strong> {selectedVisit.visitDate ? dayjs(selectedVisit.visitDate).format('DD MMMM YYYY hh:mm A') : 'N/A'}
              </div>
              <div>
                <strong>Sales Person:</strong> {selectedVisit.staffName || selectedVisit.salesPerson || 'N/A'}
              </div>
              <div>
                <strong>Visit Type:</strong> {selectedVisit.visitType || 'N/A'}
              </div>
              <div>
                <strong>Client Name:</strong> {selectedVisit.clientName || 'N/A'}
              </div>
              <div>
                <strong>Phone:</strong> {selectedVisit.phone || 'N/A'}
              </div>
              <div>
                <strong>Client Type:</strong> {selectedVisit.clientType || 'N/A'}
              </div>
              <div>
                <strong>Location:</strong> {selectedVisit.location || 'N/A'}
              </div>
              <div>
                <strong>Client OTP:</strong> {selectedVisit.clientOtp || 'N/A'}
              </div>
              {(Number.isFinite(Number(selectedVisit.checkInLat)) && Number.isFinite(Number(selectedVisit.checkInLng))) && (
                <div>
                  <strong>Check-in Location:</strong> Lat: {selectedVisit.checkInLat}, Lng: {selectedVisit.checkInLng}
                  {selectedVisit.checkInAltitude ? `, Alt: ${selectedVisit.checkInAltitude}m` : ''}
                </div>
              )}
              {selectedVisit.checkInAddress && (
                <div>
                  <strong>Check-in Address:</strong> {selectedVisit.checkInAddress}
                </div>
              )}
              <div>
                <strong>Status:</strong> {selectedVisit.status || 'Completed'}
              </div>
              <div>
                <strong>Created At:</strong> {selectedVisit.createdAt ? dayjs(selectedVisit.createdAt).format('DD MMMM YYYY hh:mm A') : 'N/A'}
              </div>
              {selectedVisit.updatedAt && (
                <div>
                  <strong>Updated At:</strong> {dayjs(selectedVisit.updatedAt).format('DD MMMM YYYY hh:mm A')}
                </div>
              )}
            </div>

            {selectedVisit.clientSignature && (
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                <strong>Client Signature:</strong>
                <div style={{ marginTop: '8px' }}>
                  <img
                    src={selectedVisit.clientSignature}
                    alt="Client Signature"
                    style={{ maxWidth: '100%', maxHeight: '200px', border: '1px solid #d9d9d9', borderRadius: '4px' }}
                  />
                </div>
              </div>
            )}

            {selectedVisit.attachments && selectedVisit.attachments.length > 0 && (
              <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
                <strong>Attachments:</strong>
                <div style={{ marginTop: '8px' }}>
                  {selectedVisit.attachments.map((attachment, index) => (
                    <div key={index} style={{ padding: '8px', backgroundColor: '#f9f9f9', marginBottom: '4px', borderRadius: '4px' }}>
                      {attachment.name || `Attachment ${index + 1}`}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div>No visit details found</div>
          </div>
        )}
      </Modal>

    </Layout>
  );
}
